import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import RunsList from './pages/RunsList';
import RunDetails from './pages/RunDetails';
import NewRun from './pages/NewRun';
import RobotsList from './pages/RobotsList';
import RobotUpload from './pages/RobotUpload';
import RobotDetail from './pages/RobotDetail';
import Docs from './pages/Docs';
import Settings from './pages/Settings';
import GitHubCallback from './pages/GitHubCallback';
import Login from './pages/Login';
import Signup from './pages/Signup';
import DebugLogs from './pages/DebugLogs';
import Demo from './pages/Demo';
// TODO: Workers + Admin panels disabled — moving to admin.canard.cloud
// import Workers from './pages/Workers';
// import Admin from './pages/Admin';
// import AdminRoute from './components/AdminRoute';
import { darkTheme } from './theme';
import { useAuth } from './contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';

const RootRedirect: React.FC = () => {
  const { isAuthenticated, isLoading, isReady } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isReady || isLoading) return;

    if (isAuthenticated) {
      navigate('/runs', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, isReady, navigate]);

  if (isLoading || !isReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return null;
};

function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/debug-logs" element={<ProtectedRoute><DebugLogs /></ProtectedRoute>} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/github/callback" element={<ProtectedRoute><GitHubCallback /></ProtectedRoute>} />
            <Route path="/" element={<RootRedirect />} />
            <Route
              path="/runs"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RunsList />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/runs/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <NewRun />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/runs/:slug"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RunDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/robots"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RobotsList />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/robots/upload"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RobotUpload />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/robots/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RobotDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Workers + Admin panels disabled — moving to admin.canard.cloud */}
            <Route
              path="/docs"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Docs />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Redirects for removed pages */}
            <Route path="/api-keys" element={<Navigate to="/settings" replace />} />
            <Route path="/plan" element={<Navigate to="/runs" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
